import type {
	Interactions,
	InteractionReactions,
	Transitions,
	Styles,
	VariantId,
	ReactionTypes,
	TriggerType,
	ModelData,
	InteractionsConfig,
	EffectId,
	AnimationOptions,
	InteractionEffects,
	RangeOffset,
	Spx,
	VariantIdToStyleProperties,
	TransformationsFromCarmi,
	ResolvedEffectRefs,
	AnimationTriggerParams,
	TransitionMethod,
	TriggerParams,
	Condition,
	ReactionDataFromCarmi,
	TriggerFromCarmi,
	ComponentId,
	BreakpointRange,
	EffectRef,
	Effect,
	EffectIdToEffectsInRefArray,
} from '@wix/thunderbolt-becky-types'
import { SkinDefaultsData } from './themeData/skinsDefaultProperties'
import { getStyleCssVars } from './themeData/themeDataCss'
import { getCSSObjectFromTransformationItem } from './transformationsFunctionLibrary'
import { BeckyModel } from '../types'

const NO_BP_KEY = 'NO_BP_KEY'
const NO_STATE_METHOD = 'NO_STATE_METHOD'

const STATE_METHODS: { [key in ReactionTypes]?: TransitionMethod } = {
	ToggleState: 'toggle',
	AddState: 'add',
	RemoveState: 'remove',
	RemoveAllStates: 'clear',
}

const KEBAB_TO_CAMEL_MAP: { [key: string]: TriggerType } = {
	click: 'click',
	hover: 'hover',
	'viewport-enter': 'viewEnter',
	'view-progress': 'viewProgress',
	'animation-end': 'animationEnd',
	'pointer-move': 'pointerMove',
	'page-visible': 'pageVisible',
	'mouse-in': 'hover',
}

const ANIMATION_PROPERTIES_MAP: { [key: string]: string } = {
	startOffset: 'rangeStart',
	endOffset: 'rangeEnd',
}

const omitProperties = (props: Array<string>, obj: Record<string, any>) => {
	return Object.keys(obj).reduce((acc, key) => {
		if (!props.includes(key)) {
			if (typeof obj[key] === 'object' && obj[key] !== null) {
				acc[key] = omitProperties(['id', 'metaData'], obj[key])
			} else {
				acc[key] = obj[key]
			}
		}
		return acc
	}, {} as Record<string, any>)
}

const buildStyleProperties = (propertiesObject: { [p: string]: string | number }) => {
	return Object.entries(propertiesObject).map(([name, value]) => ({ name, value: `${value}` }))
}

const transformationFixer = (transformationsFromCarmi: TransformationsFromCarmi): VariantIdToStyleProperties => {
	return Object.entries(transformationsFromCarmi).reduce((acc: VariantIdToStyleProperties, [key, value]) => {
		acc[key as VariantId] = buildStyleProperties(
			// https://wix.atlassian.net/browse/TB-11438 -- passing empty array because ignored transform properties are not supported right now
			getCSSObjectFromTransformationItem(value.transformationData, [], value.rotationInDegrees)
		)

		return acc
	}, {})
}

const stylesFixer = (
	styles: Styles,
	isScalePropExperimentOn: boolean,
	themeSpx: Spx | undefined,
	siteColors: Array<string>,
	componentsSkinParams?: Record<string, SkinDefaultsData>
): VariantIdToStyleProperties => {
	return Object.entries(styles).reduce((acc: Record<string, any>, [key, value]) => {
		const cssVars = getStyleCssVars(value, isScalePropExperimentOn, themeSpx, siteColors, componentsSkinParams)
		acc[key] = buildStyleProperties(cssVars)
		return acc
	}, {})
}

const animationOptionsFixer = (effectOptions: AnimationOptions): AnimationOptions => {
	return Object.entries(effectOptions).reduce((acc: Record<string, any>, [key, value]) => {
		if (key === 'startOffset' || key === 'endOffset') {
			const offset = value as RangeOffset
			acc[ANIMATION_PROPERTIES_MAP[key]] = offset
		} else if (key === 'iterations' && value === 0) {
			acc[key] = 'Infinity'
		} else {
			acc[key] = value
		}
		return acc
	}, {}) as AnimationOptions
}

const effectsFixer = (effectIdToEffectsInRefArray: EffectIdToEffectsInRefArray): EffectIdToEffectsInRefArray => {
	const propertiesToRemove = ['id', 'type', 'metaData', 'allowReplay']

	return Object.keys(effectIdToEffectsInRefArray).reduce((acc, effectId) => {
		const effectsInRefArray = effectIdToEffectsInRefArray[effectId as EffectId]

		acc[effectId as EffectId] = Object.keys(effectsInRefArray).reduce((innerAcc, effectIdInRefArray) => {
			const effectInRefArray = effectsInRefArray[effectIdInRefArray as EffectId]

			innerAcc[effectIdInRefArray as EffectId] = {
				...effectInRefArray,
				effectOptions: animationOptionsFixer(
					omitProperties(propertiesToRemove, effectInRefArray.effectOptions) as AnimationOptions
				),
			}

			return innerAcc
		}, {} as Record<EffectId, { effectOptions: AnimationOptions; bpRange?: BreakpointRange }>)

		return acc
	}, {} as EffectIdToEffectsInRefArray)
}

const responsiveEffectsFixer = (
	effectIdToEffectsInRefArray: EffectIdToEffectsInRefArray,
	reactions: InteractionReactions
) => {
	const effects: InteractionEffects = {}
	const fixedReactions: InteractionReactions = {}

	Object.entries(reactions).reduce((acc, [variantId, reactionArray]) => {
		acc[variantId as VariantId] = []
		reactionArray.forEach((reaction) => {
			if (reaction.effect) {
				const effectsFromRefArray = effectIdToEffectsInRefArray[reaction.effect]
				Object.entries(effectsFromRefArray).forEach(([effectId, effectData]) => {
					acc[variantId as VariantId].push({
						target: reaction.target,
						type: reaction.type,
						effect: effectId as EffectId,
						...(effectData.bpRange ? { bpRange: effectData.bpRange } : {}),
					})
				})
			} else {
				acc[variantId as VariantId].push(reaction)
			}
		})
		return acc
	}, fixedReactions)

	Object.values(effectIdToEffectsInRefArray).reduce((acc, effectsFromRefArrayEntry) => {
		Object.entries(effectsFromRefArrayEntry).forEach(([effectId, effectData]) => {
			acc[effectId as EffectId] = { ...effectData.effectOptions }
		})

		return acc
	}, effects)

	return { effects, reactions: fixedReactions }
}

const toMilliseconds = (number: number) => number * 1000

const trimHash = (s: string) => (s.startsWith('#') ? s.slice(1) : s)

const convertTransitionDurations = (transitions: Transitions) => {
	return Object.entries(transitions).reduce((acc: Transitions, [key, value]) => {
		const { duration, ...rest } = value
		acc[key as VariantId] = {
			...(duration && { duration: toMilliseconds(duration) }),
			...rest,
		}
		return acc
	}, {})
}

const collectReactions = (reactionObjects: Array<InteractionReactions>) => {
	return reactionObjects.reduce((acc, reactionObject) => {
		const [variantId] = Object.keys(reactionObject) as Array<VariantId>
		const reactions = reactionObject[variantId]
		if (!acc[variantId]) {
			acc[variantId] = []
		}
		acc[variantId] = acc[variantId].concat(reactions)
		return acc
	}, {})
}

const getState = (
	stateVariantId: VariantId,
	transformations: VariantIdToStyleProperties,
	transitions: Transitions,
	styles: VariantIdToStyleProperties
) => {
	return {
		transition: {
			styleProperties: [...(styles[stateVariantId] || []), ...(transformations[stateVariantId] || [])],
			...transitions[stateVariantId],
		},
	}
}

const getMatchMediaString = (range: BreakpointRange): string => {
	const min = range.min ? `(min-width:${range.min}px)` : ''
	const max = range.max ? `(max-width:${range.max}px)` : ''
	const and = min && max ? ' and ' : ''
	return `${min}${and}${max}`
}

const isValidTriggerType = (trigger: string) => {
	return KEBAB_TO_CAMEL_MAP.hasOwnProperty(trigger)
}

const createBaseInteraction = (trigger: TriggerFromCarmi) => ({
	source: `#${trigger.componentId}`,
	trigger: KEBAB_TO_CAMEL_MAP[trigger.trigger],
})

const buildInteractionsConfig = (data: ModelData): InteractionsConfig => {
	if (!data.triggers) {
		return {
			interactions: [],
			effects: {},
			conditions: {},
		}
	}

	const interactions: Interactions = []
	const effects: ResolvedEffectRefs = {}
	const conditions: Record<VariantId, Condition> = {}

	const processTrigger = (trigger: TriggerFromCarmi): void => {
		if (!isValidTriggerType(trigger.trigger)) {
			return
		}

		const reactionsGroupedByBpVariantId = (data.reactions[trigger.id] || []).reduce(
			(acc: Record<string, Array<ReactionDataFromCarmi>>, reaction) => {
				const key = reaction.bpRange ? reaction.bpRange.id : NO_BP_KEY

				if (!acc[key]) {
					acc[key] = []
				}
				acc[key].push(reaction)

				key !== NO_BP_KEY &&
					(conditions[key as VariantId] = {
						type: 'media',
						predicate: getMatchMediaString(reaction.bpRange!),
					})

				return acc
			},
			{}
		)

		Object.entries(reactionsGroupedByBpVariantId).forEach(([conditionId, variantRelationReactions]) => {
			if (conditionId === NO_BP_KEY) {
				proccessReaction(trigger, variantRelationReactions)
			} else {
				proccessReaction(trigger, variantRelationReactions, [conditionId])
			}
		})
	}

	const proccessReaction = (
		trigger: TriggerFromCarmi,
		variantRelationReactions: Array<ReactionDataFromCarmi>,
		conditionIds?: Array<string>
	): void => {
		const processAnimationEndTrigger = (): void => {
			const triggerParams = trigger.params as AnimationTriggerParams
			interactions.push({
				...createBaseInteraction(trigger),
				...(conditionIds ? { conditions: conditionIds } : {}),
				...(triggerParams.effect && {
					params: {
						effectId: trimHash(triggerParams.effect),
					},
				}),
				effects: getEffectRefs(variantRelationReactions),
			})
		}

		const processParameterizedTrigger = (): void => {
			const params = trigger?.params?.hasOwnProperty('threshold')
				? { ...trigger.params, type: 'once' as const }
				: (trigger.params as TriggerParams)

			interactions.push({
				...createBaseInteraction(trigger),
				...(conditionIds ? { conditions: conditionIds } : {}),
				...(Object.keys(params).length && { params }),
				effects: getEffectRefs(variantRelationReactions),
			})
		}

		const processViewProgressTrigger = (): void => {
			interactions.push({
				...createBaseInteraction(trigger),
				...(conditionIds ? { conditions: conditionIds } : {}),
				effects: getEffectRefs(variantRelationReactions),
			})
		}

		const processStateTrigger = (): void => {
			const stateEffectsMap = variantRelationReactions.reduce((acc, currEffect) => {
				const key = currEffect.type in STATE_METHODS ? currEffect.type : NO_STATE_METHOD

				if (!acc[key]) {
					acc[key] = []
				}
				acc[key].push(currEffect)
				return acc
			}, {} as Record<string, Array<ReactionDataFromCarmi>>)

			Object.entries(STATE_METHODS).forEach(([stateType, method]) => {
				const stateEffects = stateEffectsMap[stateType]
				if (stateEffects?.length > 0) {
					interactions.push({
						...createBaseInteraction(trigger),
						...(conditionIds ? { conditions: conditionIds } : {}),
						params: trigger.trigger === 'hover' ? { method: 'toggle' } : { method },
						effects: getEffectRefs(stateEffectsMap[stateType]),
					})
				}
			})

			if (stateEffectsMap[NO_STATE_METHOD]) {
				interactions.push({
					...createBaseInteraction(trigger),
					...(conditionIds ? { conditions: conditionIds } : {}),
					effects: getEffectRefs(stateEffectsMap[NO_STATE_METHOD]),
				})
			}
		}

		const processMouseInTrigger = (): void => {
			interactions.push({
				...createBaseInteraction(trigger),
				...(conditionIds ? { conditions: conditionIds } : {}),
				params: { method: 'toggle' },
				effects: getEffectRefs(variantRelationReactions),
			})
		}

		if (trigger.params) {
			if (trigger.trigger === 'animation-end') {
				processAnimationEndTrigger()
			} else {
				processParameterizedTrigger()
			}
		} else if (trigger.trigger === 'view-progress') {
			processViewProgressTrigger()
		} else if (trigger.trigger === 'mouse-in') {
			processMouseInTrigger()
		} else {
			processStateTrigger()
		}
	}

	const getEffectRefs = (variantRelationReactions: Array<ReactionDataFromCarmi>) => {
		const effectRefs: Array<EffectRef> = []
		const resolvedEffectRefs: Record<VariantId | EffectId, Effect> = {}
		if (!variantRelationReactions) {
			return effectRefs
		}
		variantRelationReactions.forEach((reaction) => {
			const id = reaction.state || reaction.effect
			const resolvedEffectRef = {
				...(reaction.state && getState(reaction.state, data.transformations, data.transitions, data.styles)),
				...(reaction.effect && data.effects[reaction.effect]),
			}
			if (id) {
				resolvedEffectRefs[id] = resolvedEffectRef
				effectRefs.push({
					target: `#${reaction.target}`,
					effectId: id,
				})
			}
		})

		Object.assign(effects, resolvedEffectRefs)

		return effectRefs
	}

	data.triggers.forEach((trigger) => processTrigger(trigger))

	return {
		effects,
		conditions,
		interactions,
	}
}

const prefixTarget = (target: string | undefined, prefix: string) => {
	return prefix ? `${prefix}/${target}` : target
}

const prefixEffectId = (effectId: string, prefix: string) => {
	return prefix ? `${prefix}-${effectId}` : effectId
}

const prefixConditions = (conditions: Record<string, Condition> | undefined, prefix: string) => {
	if (!conditions) {
		return {}
	}

	if (!prefix) {
		return conditions
	}

	return Object.entries(conditions).reduce((acc, [key, value]) => {
		acc[`${prefix}-${key}`] = value
		return acc
	}, {} as Record<string, Condition>)
}

const deepCopyObject = <T>(obj: T): T => {
	if (obj === null || typeof obj !== 'object') {
		return obj
	}

	if (obj instanceof Date) {
		return new Date(obj.getTime()) as T
	}

	if (Array.isArray(obj)) {
		return obj.map((item) => deepCopyObject(item)) as T
	}

	if (typeof obj === 'object') {
		const copied = {} as T
		for (const key in obj) {
			if (obj.hasOwnProperty(key)) {
				copied[key] = deepCopyObject(obj[key])
			}
		}
		return copied
	}

	return obj
}

export function deepCopyInteractionsConfig(config: InteractionsConfig): InteractionsConfig {
	if (!config || typeof config !== 'object') {
		return config
	}

	return {
		effects: config.effects ? deepCopyObject(config.effects) : {},

		conditions: config.conditions ? deepCopyObject(config.conditions) : {},

		interactions: config.interactions ? deepCopyObject(config.interactions) : [],
	}
}

/**
 * Merges two interaction configs by mutating the baseInteractionConfig.
 *
 * @param baseInteractionConfig - The base interaction config to merge into.
 * @param outerInteractionConfig - The outer interaction config to merge from.
 * @param outerConfigPrefix - The prefix to add to the target and effectId.
 * @returns A reference to baseInteractionConfig after it was merged with outerInteractionConfig.
 */
const mergeInteractionConfig = (
	baseInteractionConfig: InteractionsConfig,
	outerInteractionConfig: InteractionsConfig,
	outerConfigPrefix: string = ''
): InteractionsConfig => {
	if (!outerInteractionConfig) {
		return baseInteractionConfig
	}

	baseInteractionConfig.conditions = {
		...baseInteractionConfig.conditions,
		...prefixConditions(outerInteractionConfig.conditions, outerConfigPrefix),
	}

	Object.entries(outerInteractionConfig.effects).forEach(([effectId, effect]) => {
		const { target, ...rest } = effect
		const effectWithPrefix = {
			...rest,
			target: prefixTarget(target, outerConfigPrefix),
		}
		const effectIdWithPrefix = prefixEffectId(effectId, outerConfigPrefix)
		baseInteractionConfig.effects[effectIdWithPrefix] = effectWithPrefix
	})

	Object.values(outerInteractionConfig.interactions).forEach((interaction) => {
		const { source, effects, ...rest } = interaction
		const interactionWithPrefix = {
			source: outerConfigPrefix ? `${outerConfigPrefix}/${source}` : source,
			effects: effects.map((effect) => {
				if ('effectId' in effect) {
					// If the effect has an effectId, then it's an effectRef
					return {
						target: prefixTarget(effect.target, outerConfigPrefix),
						effectId: outerConfigPrefix ? `${outerConfigPrefix}-${effect.effectId}` : effect.effectId,
					}
				}
				const { target, ...restOfEffect } = effect
				const effectWithPrefix = {
					...restOfEffect,
					target: prefixTarget(target, outerConfigPrefix),
				}
				return effectWithPrefix
			}),
			...rest,
		}
		baseInteractionConfig.interactions.push(interactionWithPrefix)
	})

	return baseInteractionConfig
}

const mergeBuilderComponentsInteractions = (
	interactionsConfig: InteractionsConfig,
	builderComponentsInteractionsMap: BeckyModel['builderComponentsInteractions'],
	builderComponents: Record<ComponentId, string>
): InteractionsConfig => {
	if (!builderComponentsInteractionsMap) {
		return interactionsConfig
	}

	const interactionsConfigDeepCopy = deepCopyInteractionsConfig(interactionsConfig)

	return Object.entries(builderComponents).reduce((acc, [componentId, builderType]) => {
		const builderComponentInteractions = builderComponentsInteractionsMap[builderType]
		return mergeInteractionConfig(acc, builderComponentInteractions, componentId)
	}, interactionsConfigDeepCopy)
}

export {
	buildInteractionsConfig,
	collectReactions,
	effectsFixer,
	stylesFixer,
	transformationFixer,
	convertTransitionDurations,
	mergeBuilderComponentsInteractions,
	mergeInteractionConfig,
	responsiveEffectsFixer,
}
